<template>
  <div class="creation-form">
    <section class="cm-dialog" @click="closeOptionDropdown">
      <div class="cm-body">
        <div class="cm-field">
          <textarea
            v-model="textContent"
            class="cm-textarea"
            :placeholder="$t('textToSpeech.textPlaceholder')"
            rows="8"
            :maxlength="characterLimit"
          ></textarea>
          <div class="cm-textarea-meta">
            <span class="cm-count">{{ textContent.length }} / {{ characterLimit }} {{ $t('textToSpeech.characterCount') }}</span>
            <button type="button" class="cm-icon-button is-small" :title="$t('common.paste')" :aria-label="$t('common.paste')" @click="pasteFromClipboard">
              <span class="cm-icon i-paste"></span>
            </button>
          </div>
        </div>

        <div class="cm-group">
        <div class="cm-field">
          <span class="cm-label">{{ $t('textToSpeech.provider') }}</span>
          <div class="cm-segmented" role="radiogroup" :aria-label="$t('textToSpeech.provider')">
            <button
              v-for="provider in ttsProviderOptions"
              :key="provider.value"
              type="button"
              role="radio"
              :class="{ active: provider.value === voiceLibrary }"
              :aria-checked="provider.value === voiceLibrary"
              @click="selectTtsProvider(provider.value)"
            >
              {{ provider.label }}
            </button>
          </div>
          <p class="cm-hint">{{ $t('textToSpeech.providerHint') }}</p>
        </div>

        <div v-if="ttsProvider === 'openai'" class="cm-group-part">
          <div class="cm-field">
            <span class="cm-label">{{ $t('textToSpeech.voiceLabel') }}</span>
            <div class="cm-inline">
              <div :class="['cm-select', { open: openOptionDropdown === 'openaiVoice' }]" @click.stop>
                <button
                  type="button"
                  class="cm-select-button"
                  :aria-expanded="openOptionDropdown === 'openaiVoice'"
                  aria-haspopup="listbox"
                  @click="toggleOptionDropdown('openaiVoice')"
                >
                  <span class="cm-select-value">{{ selectedOpenAiVoiceLabel }}</span>
                  <span class="cm-select-arrow"></span>
                </button>
                <div v-if="openOptionDropdown === 'openaiVoice'" class="cm-select-menu is-scroll" role="listbox">
                  <button
                    v-for="voice in openAiVoiceOptions"
                    :key="voice.value"
                    type="button"
                    :class="['cm-select-option', { selected: voice.value === selectedVoice }]"
                    role="option"
                    :aria-selected="voice.value === selectedVoice"
                    @click="selectOpenAiVoice(voice.value)"
                  >
                    <span>{{ voice.label }}</span>
                    <span class="cm-select-check"></span>
                  </button>
                </div>
              </div>
              <button type="button" class="cm-icon-button" :title="$t('textToSpeech.previewButton')" :aria-label="$t('textToSpeech.previewButton')" @click="playVoicePreview">
                <span class="cm-icon i-play"></span>
              </button>
            </div>
          </div>
          <div class="cm-field">
            <span class="cm-label">{{ $t('textToSpeech.speedLabel') }}</span>
            <div class="cm-range">
              <input v-model="speed" type="range" min="0.25" max="4.0" step="0.25" :aria-label="$t('textToSpeech.speedLabel')">
              <span class="cm-range-value">{{ speed }}x</span>
            </div>
          </div>
        </div>

        <div v-if="voiceLibrary === 'google'" class="cm-group-part">
          <div class="cm-field">
            <span class="cm-label">{{ $t('textToSpeech.googleLanguage') }}</span>
            <div :class="['cm-select', { open: openOptionDropdown === 'googleLanguage' }]" @click.stop>
              <button
                type="button"
                class="cm-select-button"
                :aria-expanded="openOptionDropdown === 'googleLanguage'"
                aria-haspopup="listbox"
                @click="toggleOptionDropdown('googleLanguage')"
              >
                <span class="cm-select-value">{{ selectedGoogleLanguageLabel }}</span>
                <span class="cm-select-arrow"></span>
              </button>
              <div v-if="openOptionDropdown === 'googleLanguage'" class="cm-select-menu is-scroll" role="listbox">
                <button
                  v-for="language in googleLanguageOptions"
                  :key="language.value"
                  type="button"
                  :class="['cm-select-option', { selected: language.value === googleLanguageCode }]"
                  role="option"
                  :aria-selected="language.value === googleLanguageCode"
                  @click="selectGoogleLanguage(language.value)"
                >
                  <span>{{ language.label }}</span>
                  <span class="cm-select-check"></span>
                </button>
              </div>
            </div>
          </div>
          <div class="cm-field">
            <span class="cm-label">{{ $t('textToSpeech.googleVoice') }}</span>
            <div class="cm-inline">
              <div :class="['cm-select', { open: openOptionDropdown === 'googleVoice' }]" @click.stop>
                <button
                  type="button"
                  class="cm-select-button"
                  :aria-expanded="openOptionDropdown === 'googleVoice'"
                  aria-haspopup="listbox"
                  @click="toggleOptionDropdown('googleVoice')"
                >
                  <span class="cm-select-value">{{ selectedMultilingualVoiceLabel }}</span>
                  <span class="cm-select-arrow"></span>
                </button>
                <div v-if="openOptionDropdown === 'googleVoice'" class="cm-select-menu is-scroll" role="listbox">
                  <button
                    v-for="voice in multilingualVoiceOptions"
                    :key="voice.value"
                    type="button"
                    :class="['cm-select-option', { selected: voice.value === selectedMultilingualVoice }]"
                    role="option"
                    :aria-selected="voice.value === selectedMultilingualVoice"
                    @click="selectMultilingualVoice(voice)"
                  >
                    <span>{{ voice.label }}</span>
                    <span class="cm-select-check"></span>
                  </button>
                </div>
              </div>
              <button
                type="button"
                class="cm-icon-button"
                :disabled="!googleVoiceName"
                :title="$t('textToSpeech.playVoicePreview')"
                :aria-label="$t('textToSpeech.playVoicePreview')"
                @click="playGoogleVoicePreview"
              >
                <span class="cm-icon i-play"></span>
              </button>
            </div>
          </div>
          <div class="cm-field">
            <span class="cm-label">{{ $t('textToSpeech.speedLabel') }}</span>
            <div class="cm-range">
              <input v-model="speed" type="range" :min="speedRange.min" :max="speedRange.max" :step="speedRange.step" :aria-label="$t('textToSpeech.speedLabel')">
              <span class="cm-range-value">{{ speed }}x</span>
            </div>
          </div>
        </div>

        </div>

        <p v-if="error" class="cm-error">{{ error }}</p>
      </div>

      <footer class="cm-footer">
        <span :class="['cm-footer-meta', { 'is-insufficient': isInsufficientUsage }]">
          {{ $t('textToSpeech.usageCost', { cost: calculateUsageCost(textContent.length) }) }}
          <template v-if="isInsufficientUsage"> · {{ $t('textToSpeech.insufficientUsage') }}</template>
        </span>
        <button
          type="button"
          class="cm-button-primary"
          :disabled="!canSubmit || isProcessing || hasLengthError"
          :aria-busy="isProcessing"
          @click="handleSubmit"
        >
          <span v-if="isProcessing" class="cm-spinner" aria-hidden="true"></span>
          {{ isProcessing ? $t('textToSpeech.submittingVoice') : $t('common.generate') }}
        </button>
      </footer>
    </section>
  </div>
</template>

<script>
import { useModals } from '@/composables/modals'
import { auth } from '@/firebase'
import { trackMediaListening } from '@/utils/listeningActivity'
import { useAuth } from '@/composables/auth'
import { apiPost } from '@/composables/useApi'

// Deepgram 的英文声音，界面上不写品牌名；试听示例和多语言声音库的放在同一目录，念的是同一句话
const DEEPGRAM_VOICES = [
  { value: 'flux-alexis-en', provider: 'deepgram-flux', name: 'Alexis', gender: 'female', sample: 'en-US/Alexis_女声_Alexis.mp3' },
  { value: 'aura-2-thalia-en', provider: 'deepgram', name: 'Thalia', gender: 'female', sample: 'en-US/Thalia_女声_Thalia.mp3' },
  { value: 'aura-2-asteria-en', provider: 'deepgram', name: 'Asteria', gender: 'female', sample: 'en-US/Asteria_女声_Asteria.mp3' },
  { value: 'aura-2-orion-en', provider: 'deepgram', name: 'Orion', gender: 'male', sample: 'en-US/Orion_男声_Orion.mp3' },
];

const SPEED_RANGES = {
  'deepgram-flux': { min: 0.5, max: 1.5, step: 0.05 },
  deepgram: { min: 0.75, max: 1.5, step: 0.05 },
  google: { min: 0.25, max: 4, step: 0.25 },
  openai: { min: 0.25, max: 4, step: 0.25 },
};

export default {
  name: 'TextToSpeechUploader',
  beforeUnmount() {
    if (this.currentAudio) {
      this.currentAudio.pause()
      this.currentAudio = null
    }
  },
  setup() {
    const { userDetails } = useAuth()
    return {
      userDetails
    }
  },
  data() {
    return {
      textContent: '',
      selectedVoice: 'onyx',
      speed: 1.0,
      error: null,
      isProcessing: false,
      currentAudio: null,
      openOptionDropdown: null,
      ttsProvider: 'deepgram-flux',
      deepgramVoice: 'flux-alexis-en',
      googleLanguageCode: 'en-US',
      googleVoiceName: 'en-US-Chirp3-HD-Kore',
      googleLanguages: [],
      googleVoices: {}
    }
  },
  computed: {
    isDeepgram() {
      return ['deepgram', 'deepgram-flux'].includes(this.ttsProvider);
    },
    // 页面上只分"多语言声音库"和"自然人声"；Deepgram 的英文声音并进多语言声音库的美式英语里
    voiceLibrary() {
      return this.ttsProvider === 'openai' ? 'openai' : 'google';
    },
    ttsProviderOptions() {
      return [
        { value: 'google', label: this.$t('textToSpeech.providers.google') },
        { value: 'openai', label: this.$t('textToSpeech.providers.openai') }
      ];
    },
    multilingualVoiceOptions() {
      const google = this.googleVoiceOptions.map(voice => ({ ...voice, provider: 'google' }));
      if (this.googleLanguageCode !== 'en-US') return google;
      const deepgram = DEEPGRAM_VOICES.map(voice => ({
        value: voice.value,
        provider: voice.provider,
        label: `${this.$t(`textToSpeech.gender.${voice.gender}`)} - ${voice.name}`
      }));
      return [...deepgram, ...google];
    },
    selectedMultilingualVoice() {
      return this.isDeepgram ? this.deepgramVoice : this.googleVoiceName;
    },
    selectedMultilingualVoiceLabel() {
      return this.multilingualVoiceOptions.find(voice => voice.value === this.selectedMultilingualVoice)?.label || this.selectedMultilingualVoice;
    },
    speedRange() {
      return SPEED_RANGES[this.ttsProvider];
    },
    openAiVoiceOptions() {
      return [
        { value: 'alloy', label: 'Alloy' },
        { value: 'ash', label: 'Ash *' },
        { value: 'coral', label: 'Coral *' },
        { value: 'sage', label: 'Sage *' },
        { value: 'onyx', label: 'Onyx' },
        { value: 'echo', label: 'Echo' },
        { value: 'fable', label: 'Fable' },
        { value: 'nova', label: 'Nova' },
        { value: 'shimmer', label: 'Shimmer' }
      ];
    },
    googleLanguageOptions() {
      return this.googleLanguages.map(language => ({
        value: language.value,
        label: this.getLocalizedLabel(language.label)
      }));
    },
    googleVoiceOptions() {
      return (this.googleVoices[this.googleLanguageCode] || []).map(voice => ({
        value: voice.value,
        label: this.getLocalizedLabel(voice.label)
      }));
    },
    selectedOpenAiVoiceLabel() {
      return this.openAiVoiceOptions.find(voice => voice.value === this.selectedVoice)?.label || '';
    },
    selectedGoogleLanguageLabel() {
      return this.googleLanguageOptions.find(language => language.value === this.googleLanguageCode)?.label || this.googleLanguageCode;
    },
    selectedGoogleVoiceLabel() {
      return this.googleVoiceOptions.find(voice => voice.value === this.googleVoiceName)?.label || this.googleVoiceName;
    },
    canSubmit() {
      return Boolean(this.textContent.trim());
    },
    characterLimit() {
      return this.userDetails.isPremium ? 20000 : this.userDetails.isPro ? 10000 : 5000;
    },
    isPremium() {
      return this.userDetails.isPremium;
    },
    isPro() {
      return this.userDetails.isPro;
    },
    hasLengthError() {
      return this.textContent.length > this.characterLimit;
    },
    isInsufficientUsage() {
      return this.calculateUsageCost(this.textContent.length) > this.userDetails.remainingUsage;
    }
  },
  created() {
    // 加载Google TTS语言和语音选项
    this.loadGoogleLanguages();
    this.loadGoogleVoices();
  },
  methods: {
    toggleOptionDropdown(name) {
      this.openOptionDropdown = this.openOptionDropdown === name ? null : name;
    },
    closeOptionDropdown() {
      this.openOptionDropdown = null;
    },
    // 切到多语言声音库时，美式英语默认用列表第一个声音，其他语言用该语言的第一个声音
    selectTtsProvider(library) {
      if (library === 'openai') {
        this.setEngine('openai');
      } else {
        this.selectMultilingualVoice(this.multilingualVoiceOptions[0]);
      }
      this.closeOptionDropdown();
    },
    selectMultilingualVoice(voice) {
      if (voice.provider === 'google') {
        this.googleVoiceName = voice.value;
      } else {
        this.deepgramVoice = voice.value;
      }
      this.setEngine(voice.provider);
      this.closeOptionDropdown();
    },
    // 各家支持的语速范围不同，换声音后把语速收进新范围
    setEngine(provider) {
      this.ttsProvider = provider;
      const { min, max } = SPEED_RANGES[provider];
      this.speed = Math.min(max, Math.max(min, Number(this.speed)));
    },
    selectOpenAiVoice(voice) {
      this.selectedVoice = voice;
      this.closeOptionDropdown();
    },
    selectGoogleLanguage(language) {
      this.googleLanguageCode = language;
      this.updateGoogleVoice();
      this.selectMultilingualVoice(this.multilingualVoiceOptions[0]);
    },
    stripMarkdownForSpeech(text) {
      return String(text || '')
        .replace(/```[\s\S]*?```/g, ' ')
        .replace(/`([^`]+)`/g, '$1')
        .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .replace(/^#{1,6}\s+/gm, '')
        .replace(/^>\s?/gm, '')
        .replace(/^\s*[-*+]\s+/gm, '')
        .replace(/^\s*\d+[.)]\s+/gm, '')
        .replace(/^\s*[-:| ]{3,}\s*$/gm, '')
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/__([^_]+)__/g, '$1')
        .replace(/\*([^*]+)\*/g, '$1')
        .replace(/_([^_]+)_/g, '$1')
        .replace(/~~([^~]+)~~/g, '$1')
        .replace(/[>#*_~`|]{2,}/g, ' ')
        .replace(/[ \t]+/g, ' ')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
    },
    async handleSubmit() {
      if (!auth.currentUser) { useModals().showLoginModal(); return }
      if (this.isInsufficientUsage) { useModals().handleUpgrade(); return }
      this.isProcessing = true
      this.error = null
      
      try {
        const text = this.textContent.trim()
        const speechText = this.stripMarkdownForSpeech(text)
        
        // 准备请求数据
        const requestData = {
          requestId: globalThis.crypto?.randomUUID
            ? globalThis.crypto.randomUUID()
            : `tts-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          text: speechText,
          provider: this.ttsProvider,
          voice: this.isDeepgram ? this.deepgramVoice : this.selectedVoice,
          languageCode: this.googleLanguageCode,
          voiceName: this.googleVoiceName,
          speed: this.speed
        }

        // 生成数据库记录
        await apiPost('/api/tts/jobs', requestData)

        // 根据选择的TTS提供商发送请求
          // 使用OpenAI TTS
          // 使用Google TTS
        this.$emit('record-success')
      } catch (error) {
        this.error = error.message || this.$t('textToSpeech.errors.generic')
      } finally {
        this.isProcessing = false
      }
    },
    async pasteFromClipboard() {
      try {
        const text = await navigator.clipboard.readText();
        const remainingSpace = this.characterLimit - this.textContent.length;
        if (text.length > remainingSpace) {
          // 如果粘贴的内容超出限制，只取能容纳的部分
          this.textContent += text.slice(0, remainingSpace);
          this.error = this.$t('textToSpeech.errors.characterLimitExceeded');
        } else {
          this.textContent += text;
        }
      } catch (err) {
        this.error = this.$t('textToSpeech.errors.pasteFailed');
      }
    },
    calculateUsageCost(length) {
      if (this.ttsProvider === 'deepgram-flux') return Math.ceil(length / 10);
      if (['google', 'deepgram'].includes(this.ttsProvider)) {
        return Math.ceil(length / 15);
      }
      // OpenAI TTS的原始计算方式
      return Math.ceil(length / 30);
    },
    async playVoicePreview() {
      try {
        if (this.currentAudio) {
          this.currentAudio.pause()
        }
        const audioFile = `${this.selectedVoice}.mp3`
        this.currentAudio = new Audio(`/audio_samples/${audioFile}`)
        trackMediaListening(this.currentAudio)
        await this.currentAudio.play()
      } catch (error) {
        alert(this.$t('textToSpeech.previewError'))
      }
    },
    loadGoogleLanguages() {
      import('@/assets/google-languages.json')
        .then(module => {
          this.googleLanguages = module.default;
        })
        .catch(() => {
          this.googleLanguages = [];
        });
    },
    loadGoogleVoices() {
      import('@/assets/google-voices.json')
        .then(module => {
          this.googleVoices = module.default;
          // 设置默认语音
          if (this.googleVoices[this.googleLanguageCode] && this.googleVoices[this.googleLanguageCode].length > 0) {
            this.googleVoiceName = this.googleVoices[this.googleLanguageCode][0].value;
          }
        })
        .catch(() => {
          this.googleVoices = {};
        });
    },
    updateGoogleVoice() {
      if (this.googleVoices[this.googleLanguageCode] && this.googleVoices[this.googleLanguageCode].length > 0) {
        this.googleVoiceName = this.googleVoices[this.googleLanguageCode][0].value;
      } else {
        // 如果没有找到对应语言的语音，设置一个默认值
        this.googleVoiceName = 'en-US-Chirp3-HD-Kore';
      }
    },
    getLocalizedLabel(labelObj) {
      if (typeof labelObj === 'string') {
        return labelObj;
      }
      // 从i18n获取当前语言，默认为中文
      const currentLocale = this.$i18n?.locale || 'zh';
      return labelObj[currentLocale] || labelObj.zh || labelObj.en || '';
    },
    async playGoogleVoicePreview() {
      try {
        if (this.currentAudio) {
          this.currentAudio.pause()
        }
        
        if (this.isDeepgram) {
          const voice = DEEPGRAM_VOICES.find(v => v.value === this.deepgramVoice);
          this.currentAudio = new Audio(`/voice-samples/${voice.sample}`);
          trackMediaListening(this.currentAudio)
          await this.currentAudio.play();
          return;
        }

        // 根据语音名称生成对应的示例文件路径
        const voiceShortName = this.googleVoiceName.split("-").pop(); // 提取语音的短名称
        const voiceInfo = this.googleVoices[this.googleLanguageCode]?.find(v => v.value === this.googleVoiceName);
        if (!voiceInfo) {
          throw new Error('语音信息未找到');
        }
        
        const voiceLabel = voiceInfo.label.zh || voiceInfo.label.en;
        const cleanLabel = voiceLabel.replace(' - ', '_').replace('（', '_').replace('）', '');
        const filename = `${voiceShortName}_${cleanLabel}.mp3`;
        const audioPath = `/voice-samples/${this.googleLanguageCode}/${filename}`;
        
        this.currentAudio = new Audio(audioPath);
        trackMediaListening(this.currentAudio)
        await this.currentAudio.play();
      } catch (error) {
        // 如果找不到对应的示例文件，显示友好的错误信息
        alert(this.$t('textToSpeech.previewError') || '示例语音播放失败，请稍后再试');
      }
    }
  }
}
</script>
